import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { AuthenticationService } from './auth.service';
import { Role } from './Roles/roleEnum';

@Injectable({
  providedIn: 'root',
})
export class RoleGuard implements CanActivate {
  constructor(
    private router: Router,
    private authService: AuthenticationService
  ) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    const user = this.authService.getCurrentUser();
    const expectedRoles = (route.data['expectedRoles'] as Role[]) ?? [];
    const requiresPharmacy = route.data['requiresPharmacy'] === true;

    const clinicRaw = localStorage.getItem('currentClinic');
    const clinic = clinicRaw ? JSON.parse(clinicRaw) : null;
    const hasPharmacy = clinic?.hasPharmacy ?? false;

    if (!user) {
      await this.router.navigate(['/']);
      return false;
    }

    const userRoles = user.roles;
    const pharmacyOrOpsBypassRoles = [
      Role.Admin,
      Role.PharmacyOperator,
      Role.Pharmacist,
      Role.PharmacyManager,
      Role.StockTransfer,
      Role.OperationManager,
    ];
    const canBypassPharmacyFlag = pharmacyOrOpsBypassRoles.some((role) =>
      userRoles.includes(role)
    );

    if (expectedRoles.some((role) => userRoles.includes(role))) {
      if (requiresPharmacy && !hasPharmacy && !canBypassPharmacyFlag) {
        await this.router.navigate(['/pharmacy/no-access']);
        return false;
      }
      return true;
    }

    const roleRedirectCandidates = userRoles.filter(
      (role) => role !== Role.Doctor && role !== Role.Customer
    );

    if (roleRedirectCandidates.includes(Role.WhatsappManager)) {
      await this.router.navigate(['/messages']);
      return false;
    }

    if (roleRedirectCandidates.includes(Role.taskReviewer)) {
      await this.router.navigate(['/process/list']);
      return false;
    }

    if (roleRedirectCandidates.includes(Role.Auditor)) {
      await this.router.navigate(['/process/auditor']);
      return false;
    }

    if (roleRedirectCandidates.includes(Role.StoreManager)) {
      await this.router.navigate(['/store/inventory']);
      return false;
    }

    if (roleRedirectCandidates.includes(Role.StoreApproval)) {
      await this.router.navigate(['/store/indent']);
      return false;
    }

    // Define role groups for redirection logic
    const pharmacyRoles = [
      Role.Pharmacist,
      Role.PharmacyManager,
      Role.StockTransfer,
      Role.PharmacyOperator,
    ];
    const mixedRoles = [Role.Customer, Role.Doctor];
    const nonPharmacyStaffRoles = [
      Role.HospitalManager,
      Role.OperationManager,
      Role.Receptionist,
    ];

    const hasPharmacyRole = pharmacyRoles.some((r) => userRoles.includes(r));
    const hasMixedRole = mixedRoles.some((r) => userRoles.includes(r));
    const isNonPharmacyStaff = nonPharmacyStaffRoles.some((r) =>
      userRoles.includes(r)
    );

    if (
      hasPharmacyRole &&
      hasMixedRole &&
      !hasPharmacy &&
      !userRoles.includes(Role.Admin)
    ) {
      await this.router.navigate(['/all-appointment']);
    } else if (hasPharmacyRole && hasMixedRole && hasPharmacy) {
      await this.router.navigate(['/all-appointment']);
    } else if (isNonPharmacyStaff || userRoles.includes(Role.Admin)) {
      await this.router.navigate(['/doctors']);
    } else {
      await this.router.navigate(['/']);
    }

    return false;
  }
}
